
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import DesktopBehavioralChart from '@/components/charts/DesktopBehavioralChart';
import ContributorsTooltip from './ContributorsTooltip';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useKeyTraits } from '@/hooks/useKeyTraits';

interface ProfileShowcaseCardProps {
  pattern: string;
  profileData: {
    type: string;
    description: string;
    traits: string[];
    workStyle: string;
    communication: string;
  };
  exampleScores: {
    name: string;
    fullName: string;
    value: number;
    color: string;
    sampleSize?: number;
    contributors?: string[];
  }[];
  isRealData?: boolean;
  showSampleSize?: boolean;
}

const ProfileShowcaseCard: React.FC<ProfileShowcaseCardProps> = ({
  pattern,
  profileData,
  exampleScores,
  isRealData = false,
  showSampleSize = true
}) => {
  const { t } = useLanguage();
  const { isSuperAdmin } = useAuth();
  const { getTraitForFactor, loading: traitsLoading } = useKeyTraits();

  // Ensure exampleScores is always an array
  const scoresArray = Array.isArray(exampleScores) ? exampleScores : [];

  // Get sample size and contributors from the first score (they should all have the same data)
  const sampleSize = scoresArray[0]?.sampleSize;
  const contributors = scoresArray[0]?.contributors || [];

  // Generate dynamic traits based on the example scores using the key traits table
  const getDynamicTraits = (): string[] => {
    if (traitsLoading) {
      return ['Loading...'];
    }

    // Map the DCTOS score names to factor names
    const factorMap: Record<string, string> = {
      'D': 'dominance',     // Drive
      'C': 'extraversion',  // Connection
      'T': 'patience',      // Task
      'O': 'formality'      // Order
    };
    return scoresArray.filter(score => factorMap[score.name]) // Only include A-B-C-D factors (not E for objectivity)
    .map(score => {
      const factor = factorMap[score.name];
      return getTraitForFactor(factor, score.value);
    });
  };
  const dynamicTraits = getDynamicTraits();

  return (
    <Card className="h-full border border-border/60 rounded-lg bg-card hover:border-border transition-colors duration-200 group">
      <CardHeader className="p-6 pb-4">
        <div className="flex items-start justify-between gap-4 mb-3">
          <CardTitle className="text-lg font-medium text-card-foreground flex-1 leading-tight tracking-tight">
            {profileData.type}
          </CardTitle>
          <Badge variant="outline" className="bg-muted text-muted-foreground border-border font-mono text-xs px-2 py-1 shrink-0">
            {pattern}
          </Badge>
        </div>

        {/* Chart moved to top - Minimal design */}
        <div className="mb-4">
          <div className="flex items-center justify-end mb-3 min-h-[1rem]">
            {/* Only super admins see sample size info when showSampleSize is enabled */}
            {showSampleSize && isSuperAdmin() && (
              isRealData && sampleSize ? (
                contributors.length > 0 ? (
                  <ContributorsTooltip contributors={contributors} sampleSize={sampleSize}>
                    <p className="text-xs text-muted-foreground cursor-help underline decoration-dotted underline-offset-2">
                      {`Based on ${sampleSize} assessments`}
                    </p>
                  </ContributorsTooltip>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {`Based on ${sampleSize} assessments`}
                  </p>
                )
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('profiles.exampleData') || 'Example data'}
                </p>
              )
            )}
          </div>
          <div className="h-36 bg-muted/30 rounded border border-border/30 p-1 overflow-hidden">
            <DesktopBehavioralChart 
              data={scoresArray} 
              height={130}
              compact={true}
            />
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">
          {profileData.description}
        </p>
      </CardHeader>
      
      <CardContent className="p-6 pt-0 space-y-6">
        {/* Dynamic Traits - Linear style */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">
            {t('profile.traits') || 'Key Traits'}
          </h4>
          <div className="flex flex-wrap gap-2">
            {dynamicTraits.slice(0, 4).map((trait, index) => (
              <Badge key={index} variant="outline" className="text-xs bg-muted/50 border-border/50 text-foreground px-2 py-1">
                {trait}
              </Badge>
            ))}
          </div>
        </div>

        {/* Work Style - Clean typography */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-2">
            {t('profile.workStyle') || 'Work Style'}
          </h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {profileData.workStyle}
          </p>
        </div>

        {/* Communication - Clean typography */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-2">
            {t('profile.communication') || 'Communication'}
          </h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {profileData.communication}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileShowcaseCard;
